import { useCallback, type ReactNode, type RefObject } from "react";
import { RotateCcw, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { projectDesignerWorkspaceActions } from "@/lib/designer/workspaceActions";
import { cn } from "@/lib/utils";
import type { PendingDesignAction } from "./PendingDesignActionDialog";

interface DesignPreviewHeaderProps {
  titleId: string;
  description: string;
  detailsOpen: boolean;
  detailsPanelId: string;
  detailsTriggerRef: RefObject<HTMLButtonElement | null>;
  onDetailsOpenChange: (open: boolean) => void;
  startOverDisabled: boolean;
  onStartOver?: () => void;
  children: ReactNode;
}

export function DesignPreviewHeader({
  titleId,
  description,
  detailsOpen,
  detailsPanelId,
  detailsTriggerRef,
  onDetailsOpenChange,
  startOverDisabled,
  onStartOver,
  children,
}: DesignPreviewHeaderProps) {
  return (
    <header
      data-slot="desktop-design-workspace-header"
      className="designer-preview-header"
    >
      <div className="designer-preview-heading">
        <h2 id={titleId}>Your design</h2>
        <p title={description}>{description}</p>
      </div>
      <div className="designer-preview-toolbar">
        {children}
        <Button
          ref={detailsTriggerRef}
          type="button"
          variant="ghost"
          className="designer-control designer-details-toggle h-11 gap-2 px-3"
          data-state={detailsOpen ? "open" : "closed"}
          aria-label="Design details"
          aria-expanded={detailsOpen}
          aria-controls={detailsPanelId}
          onClick={() => onDetailsOpenChange(!detailsOpen)}
        >
          <SlidersHorizontal className="size-4" aria-hidden="true" />
          Details
        </Button>
        {onStartOver ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="designer-control designer-start-over size-11 shrink-0 text-muted-foreground"
            onClick={event => {
              // Safari does not focus buttons on pointer activation. Give the
              // confirmation dialog a reliable place to return focus.
              event.currentTarget.focus({ preventScroll: true });
              onStartOver();
            }}
            disabled={startOverDisabled}
            aria-label="Start over"
            title="Start over"
          >
            <RotateCcw className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </header>
  );
}

interface DesignSecondaryActionsProps {
  actions: ReturnType<typeof projectDesignerWorkspaceActions>;
  hasPendingChanges: boolean;
  quoteSubmitting: boolean;
  onRequest: (action: PendingDesignAction, trigger: HTMLButtonElement) => void;
}

export function DesignSecondaryActions({
  actions,
  hasPendingChanges,
  quoteSubmitting,
  onRequest,
}: DesignSecondaryActionsProps) {
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="designer-control h-11 flex-1 bg-transparent px-3 lg:flex-none"
        disabled={!actions.tryOn.enabled}
        title={actions.tryOn.unavailableReason ?? "Try on your dress for free"}
        onClick={event => onRequest("tryOn", event.currentTarget)}
        aria-label="Try on your dress for free"
      >
        Try on
      </Button>
      <Button
        type="button"
        variant="outline"
        className={cn(
          "designer-control h-11 flex-1 bg-transparent px-4 lg:flex-none",
          !hasPendingChanges && actions.quote.enabled && "designer-quote-ready"
        )}
        disabled={!actions.quote.enabled}
        title={actions.quote.unavailableReason ?? undefined}
        onClick={event => onRequest("quote", event.currentTarget)}
      >
        {quoteSubmitting ? "Preparing…" : "Get quote"}
      </Button>
    </>
  );
}

interface DesktopDesignActionsProps {
  focusFallbackRef: RefObject<HTMLButtonElement | null>;
  summaryLabel: string;
  summaryValue: string;
  failed: boolean;
  hasDesign: boolean;
  canUndo: boolean;
  onUndo: () => void;
  canRender: boolean;
  onRender: () => void;
  renderLabel: string;
  helper: string | null;
  helperId: string;
}

export function DesktopDesignActions({
  focusFallbackRef,
  summaryLabel,
  summaryValue,
  failed,
  hasDesign,
  canUndo,
  onUndo,
  canRender,
  onRender,
  renderLabel,
  helper,
  helperId,
}: DesktopDesignActionsProps) {
  const actionDockRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      return () => {
        // Revert and successful Apply remove this footer. Preserve keyboard
        // position only when focus is still inside it; never steal chat focus.
        if (node.contains(document.activeElement))
          focusFallbackRef.current?.focus();
      };
    },
    [focusFallbackRef]
  );
  return (
    <footer
      ref={actionDockRef}
      data-slot="desktop-design-actions"
      className="designer-action-dock"
    >
      <div
        className="designer-action-summary flex min-w-0 flex-col justify-center gap-0.5"
        role="status"
        aria-live="polite"
      >
        <p
          className={cn(
            "designer-summary-label truncate text-xs leading-[18px] text-muted-foreground",
            failed && "text-destructive"
          )}
          title={summaryLabel}
        >
          {summaryLabel}
        </p>
        <p
          className="designer-summary-value truncate text-sm font-medium leading-5"
          title={summaryValue}
        >
          {summaryValue}
        </p>
      </div>
      {hasDesign ? (
        <Button
          type="button"
          variant="ghost"
          className="designer-control designer-revert h-11 px-2"
          onClick={event => {
            // The footer may disappear immediately. Let its cleanup restore
            // focus consistently after pointer activation in Safari too.
            event.currentTarget.focus({ preventScroll: true });
            onUndo();
          }}
          disabled={!canUndo}
          aria-label="Revert pending changes"
          title="Restore the saved design details"
        >
          Revert
        </Button>
      ) : (
        <span className="designer-revert" aria-hidden />
      )}
      <Button
        type="button"
        className="designer-primary designer-apply h-12 w-full px-3"
        disabled={!canRender}
        onClick={onRender}
        aria-describedby={helper ? helperId : undefined}
        title={helper ?? undefined}
      >
        {renderLabel}
      </Button>
      {helper ? (
        <span id={helperId} className="sr-only">
          {helper}
        </span>
      ) : null}
    </footer>
  );
}
